import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import SearchIcon from "@material-ui/icons/Search";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import KeyboardReturnIcon from "@material-ui/icons/KeyboardReturn";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import formatPhone from "../../helpers/formatPhone";
import ContactAvatar from "../ContactAvatar";

// Páginas do sistema. "admin" = só administradores (mesma regra do menu),
// "plan" = recurso do plano que precisa estar ativo, "super" = só superadmin.
const PAGES = [
  { label: "Dashboard", path: "/", admin: true, keywords: "inicio painel visao geral" },
  { label: "Chats", path: "/tickets", keywords: "tickets atendimentos conversas whatsapp" },
  { label: "Contatos", path: "/contacts", keywords: "clientes" },
  { label: "Kanban", path: "/kanban", plan: "useKanban", keywords: "quadro funil" },
  { label: "CRM", path: "/crm", keywords: "negocios oportunidades vendas" },
  { label: "Relatórios do CRM", path: "/crm/reports", keywords: "negocios vendas" },
  { label: "Tarefas", path: "/todolist", keywords: "todo lista" },
  { label: "Chat interno", path: "/chats", plan: "useInternalChat", keywords: "equipe mensagens" },
  { label: "Respostas rápidas", path: "/quick-messages", keywords: "atalhos mensagens prontas" },
  { label: "Etiquetas", path: "/tags", keywords: "tags" },
  { label: "Agendamentos de mensagens", path: "/schedules", plan: "useSchedules", keywords: "lembretes" },
  { label: "Agenda", path: "/appointments", keywords: "agendamentos horarios" },
  { label: "Bloqueios da agenda", path: "/schedule-blocks", keywords: "folgas horarios" },
  { label: "Dashboard da agenda", path: "/appointment-dashboard", keywords: "relatorio agenda" },
  { label: "Financeiro", path: "/financeiro", admin: true, keywords: "faturas pagamento assinatura" },
  { label: "Campanhas", path: "/campaigns", admin: true, plan: "useCampaigns", keywords: "disparos marketing" },
  { label: "Listas de contatos", path: "/contact-lists", admin: true, plan: "useCampaigns", keywords: "campanhas" },
  { label: "Configurações de campanhas", path: "/campaigns-config", admin: true, plan: "useCampaigns", keywords: "marketing" },
  { label: "Conexões", path: "/connections", admin: true, keywords: "whatsapp qrcode numero" },
  { label: "Departamentos", path: "/queues", admin: true, keywords: "filas setores" },
  { label: "Usuários", path: "/users", admin: true, keywords: "atendentes equipe" },
  { label: "Serviços", path: "/services", admin: true, keywords: "agenda" },
  { label: "Arquivos", path: "/files", admin: true, keywords: "anexos" },
  { label: "Prompts de IA", path: "/prompts", admin: true, plan: "useOpenAi", keywords: "inteligencia artificial openai" },
  { label: "Integrações", path: "/queue-integration", admin: true, plan: "useIntegrations", keywords: "n8n typebot webhook" },
  { label: "API de mensagens", path: "/messages-api", admin: true, plan: "useExternalApi", keywords: "desenvolvedor" },
  { label: "Configurações", path: "/settings", admin: true, keywords: "opcoes preferencias" },
  { label: "Comunicados", path: "/announcements", super: true, keywords: "avisos" },
  { label: "Sugestões", path: "/suggestions", super: true, keywords: "melhorias" },
  { label: "Central de ajuda", path: "/helps", keywords: "suporte duvidas tutoriais" },
];

const STATUS_LABEL = { open: "Atendendo", pending: "Aguardando", closed: "Resolvido" };
const STATUS_COLOR = { open: "#2D7BE8", pending: "#F08A3C", closed: "#16A34A" };
const MAX_RESULTS = 5;

// Compara sem acento e sem diferença de maiúsculas
const normalize = (text) =>
  (text || "").toString().normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform || "");

const useStyles = makeStyles((theme) => ({
  // Botão que parece um campo de busca, na barra superior
  trigger: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    height: 36,
    width: 380,
    maxWidth: "36vw",
    padding: "0 8px 0 10px",
    marginLeft: 16,
    borderRadius: 10,
    cursor: "pointer",
    font: "inherit",
    fontSize: "0.85rem",
    textAlign: "left",
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#F0F3F8",
    border: `1px solid ${theme.palette.divider}`,
    "&:hover": { borderColor: theme.palette.primary.main },
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
    [theme.breakpoints.down("sm")]: {
      width: 36,
      padding: 0,
      justifyContent: "center",
      marginLeft: 8,
    },
  },
  triggerText: {
    flex: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [theme.breakpoints.down("sm")]: { display: "none" },
  },
  kbd: {
    fontSize: 11,
    fontWeight: 600,
    fontFamily: "inherit",
    padding: "1px 6px",
    borderRadius: 5,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: { display: "none" },
  },

  // Janela presa no alto da tela, para não "pular" enquanto os resultados mudam
  container: {
    alignItems: "flex-start",
  },
  paper: {
    width: 620,
    maxWidth: "calc(100vw - 32px)",
    margin: "12vh 16px 16px",
    borderRadius: 14,
    overflow: "hidden",
  },
  inputRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  input: { flex: 1, fontSize: "1rem" },
  results: {
    maxHeight: "56vh",
    overflowY: "auto",
    padding: "6px 8px 10px",
    ...theme.scrollbarStyles,
  },
  groupTitle: {
    padding: "10px 10px 4px",
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    padding: "8px 10px",
    border: 0,
    borderRadius: 9,
    cursor: "pointer",
    font: "inherit",
    textAlign: "left",
    color: theme.palette.text.primary,
    backgroundColor: "transparent",
  },
  itemActive: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(45,123,232,0.22)" : "#EAF2FD",
  },
  itemIcon: {
    width: 32,
    height: 32,
    borderRadius: 9,
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#F0F3F8",
  },
  itemBody: { flex: 1, minWidth: 0 },
  itemTitle: {
    fontSize: "0.88rem",
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  itemSub: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  status: {
    fontSize: "0.68rem",
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: 999,
    flexShrink: 0,
  },
  enterHint: { color: theme.palette.text.secondary, fontSize: 16, flexShrink: 0 },
  empty: {
    padding: "26px 16px",
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
  },
  footer: {
    display: "flex",
    gap: 16,
    padding: "8px 16px",
    borderTop: `1px solid ${theme.palette.divider}`,
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    [theme.breakpoints.down("sm")]: { display: "none" },
  },
}));

const GlobalSearch = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [contacts, setContacts] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const [plan, setPlan] = useState(null);
  const listRef = useRef(null);

  // Ctrl+K (ou Cmd+K no Mac) abre de qualquer tela
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Recursos do plano: carrega uma vez, na primeira abertura
  const planRequested = useRef(false);
  useEffect(() => {
    if (!open || planRequested.current || !user?.companyId) return;
    planRequested.current = true;
    getPlanCompany(undefined, user.companyId)
      .then((data) => setPlan(data?.plan || {}))
      .catch(() => setPlan({}));
    // getPlanCompany muda a cada render; o ref garante uma chamada só
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  // Limpa ao fechar
  useEffect(() => {
    if (!open) {
      setQuery("");
      setContacts([]);
      setTickets([]);
      setActive(0);
    }
  }, [open]);

  // Contatos e atendimentos: busca no servidor depois de uma pausa na digitação
  useEffect(() => {
    const term = query.trim();
    if (!open || term.length < 2) {
      setContacts([]);
      setTickets([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      const queueIds = (user?.queues || []).map((q) => q.id);
      const [contactsRes, ticketsRes] = await Promise.allSettled([
        api.get("/contacts", { params: { searchParam: term, pageNumber: 1 } }),
        api.get("/tickets", {
          params: { searchParam: term.toLowerCase(), pageNumber: 1, showAll: "true", queueIds: JSON.stringify(queueIds) },
        }),
      ]);
      if (cancelled) return;
      setContacts(contactsRes.status === "fulfilled" ? (contactsRes.value.data?.contacts || []).slice(0, MAX_RESULTS) : []);
      setTickets(ticketsRes.status === "fulfilled" ? (ticketsRes.value.data?.tickets || []).slice(0, MAX_RESULTS) : []);
      setLoading(false);
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, open, user]);

  const pages = useMemo(() => {
    const isAdmin = user?.profile === "admin";
    const allowed = PAGES.filter((p) => {
      if (p.super && !user?.super) return false;
      if (p.admin && !isAdmin) return false;
      if (p.plan && plan && plan[p.plan] === false) return false;
      return true;
    });
    const term = normalize(query.trim());
    if (!term) return allowed.slice(0, 6);
    return allowed
      .filter((p) => normalize(`${p.label} ${p.keywords || ""}`).includes(term))
      .slice(0, MAX_RESULTS);
  }, [query, user, plan]);

  // Lista única para navegar com as setas
  const items = useMemo(() => [
    ...pages.map((p) => ({ type: "page", key: `p-${p.path}`, data: p })),
    ...contacts.map((c) => ({ type: "contact", key: `c-${c.id}`, data: c })),
    ...tickets.map((t) => ({ type: "ticket", key: `t-${t.id}`, data: t })),
  ], [pages, contacts, tickets]);

  useEffect(() => { setActive(0); }, [query]);

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${active}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [active]);

  const go = useCallback((item) => {
    if (!item) return;
    setOpen(false);
    if (item.type === "page") history.push(item.data.path);
    if (item.type === "contact") history.push(`/contacts/${item.data.id}/profile`);
    if (item.type === "ticket") history.push(`/tickets/${item.data.uuid}`);
  }, [history]);

  const onInputKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(items[active]);
    }
  };

  const renderItem = (item, index) => {
    const isActive = index === active;
    let icon;
    let title;
    let sub;
    let extra = null;

    if (item.type === "page") {
      icon = <div className={classes.itemIcon}><ArrowForwardIcon fontSize="small" /></div>;
      title = item.data.label;
      sub = item.data.path === "/" ? "Página inicial" : "Página";
    } else if (item.type === "contact") {
      const c = item.data;
      icon = c.profilePicUrl || c.name
        ? <ContactAvatar src={c.profilePicUrl} name={c.name} style={{ width: 32, height: 32 }} />
        : <div className={classes.itemIcon}><PersonOutlineIcon fontSize="small" /></div>;
      title = c.name || formatPhone(c.number);
      sub = [c.number ? formatPhone(c.number) : null, c.email || null].filter(Boolean).join(" · ") || "Contato";
    } else {
      const t = item.data;
      const color = STATUS_COLOR[t.status] || "#7C7C7C";
      icon = <div className={classes.itemIcon}><ChatBubbleOutlineIcon fontSize="small" /></div>;
      title = `${t.contact?.name || "Contato"} · #${t.id}`;
      sub = [t.queue?.name || "Sem departamento", t.user?.name || null, t.lastMessage || null].filter(Boolean).join(" · ");
      extra = (
        <span className={classes.status} style={{ color, backgroundColor: `${color}1F` }}>
          {STATUS_LABEL[t.status] || t.status}
        </span>
      );
    }

    return (
      <button
        type="button"
        key={item.key}
        data-index={index}
        className={`${classes.item} ${isActive ? classes.itemActive : ""}`}
        onMouseEnter={() => setActive(index)}
        onClick={() => go(item)}
      >
        {icon}
        <div className={classes.itemBody}>
          <div className={classes.itemTitle}>{title}</div>
          <div className={classes.itemSub}>{sub}</div>
        </div>
        {extra}
        {isActive && <KeyboardReturnIcon className={classes.enterHint} />}
      </button>
    );
  };

  const term = query.trim();
  let index = -1;
  const group = (title, list) => {
    if (list.length === 0) return null;
    return (
      <div>
        <div className={classes.groupTitle}>{title}</div>
        {list.map(() => {
          index += 1;
          return renderItem(items[index], index);
        })}
      </div>
    );
  };

  return (
    <>
      <button type="button" className={classes.trigger} onClick={() => setOpen(true)} aria-label="Buscar">
        <SearchIcon fontSize="small" />
        <span className={classes.triggerText}>Buscar contatos, tickets ou páginas</span>
        <span className={classes.kbd}>{isMac ? "⌘" : "Ctrl"}</span>
        <span className={classes.kbd}>K</span>
      </button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        classes={{ container: classes.container, paper: classes.paper }}
        maxWidth={false}
      >
        <div className={classes.inputRow}>
          {loading ? <CircularProgress size={20} /> : <SearchIcon color="action" />}
          <InputBase
            autoFocus
            className={classes.input}
            placeholder="Buscar contatos, tickets ou páginas"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            inputProps={{ "aria-label": "Buscar no sistema" }}
          />
          <span className={classes.kbd}>Esc</span>
        </div>

        <div className={classes.results} ref={listRef}>
          {group(term ? "Páginas" : "Ir para", pages)}
          {group("Contatos", contacts)}
          {group("Atendimentos", tickets)}
          {term.length >= 2 && !loading && items.length === 0 && (
            <div className={classes.empty}>Nada encontrado para "{term}".</div>
          )}
          {term.length === 1 && pages.length === 0 && (
            <div className={classes.empty}>Digite pelo menos 2 letras para buscar contatos e tickets.</div>
          )}
        </div>

        <div className={classes.footer}>
          <span>↑ ↓ para navegar</span>
          <span>Enter para abrir</span>
          <span>Esc para fechar</span>
        </div>
      </Dialog>
    </>
  );
};

export default GlobalSearch;
